import type { GuidedVendorDemo, GuidedVendorDemoAction, PrivateVendor, PrivateVendorWorkspace, VendorDemoAssignment, VendorDemoContractor, VendorInvitation } from './private-vendor-types'

export const GUIDED_DEMO_SCHEMA_VERSION = 'property-manager-v2'
export const VENDOR_CLIENT_STATE_VERSION = '2'
const clientStateVersionKey = 'propertyready.vendor-workspace.schema'
const obsoleteClientKeys = ['propertyready.guided-demo.state', 'propertyready.demo-state', 'propertyready.workspace-mode']

export function migrateVendorClientState(storage: Pick<Storage, 'getItem'|'setItem'|'removeItem'> = window.localStorage) {
  if (storage.getItem(clientStateVersionKey) === VENDOR_CLIENT_STATE_VERSION) return false
  obsoleteClientKeys.forEach((key) => storage.removeItem(key))
  storage.setItem(clientStateVersionKey, VENDOR_CLIENT_STATE_VERSION)
  return true
}

const object = (value: unknown): Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
const text = (value: unknown, fallback = '') => typeof value === 'string' && value.trim() ? value : fallback
const number = (value: unknown) => typeof value === 'number' && Number.isFinite(value) ? value : 0
const boolean = (value: unknown) => value === true
const list = (value: unknown): unknown[] => Array.isArray(value) ? value as unknown[] : []

const normalizeDemoContractor = (value: unknown, index: number): VendorDemoContractor => {
  const row = object(value)
  return {
    id: text(row.id, `demo-contractor-${index + 1}`), name: text(row.name, 'Demo contractor'),
    category: text(row.category, 'Other property services'), preferred: boolean(row.preferred),
    status: text(row.status, 'pending_review'), insurance: text(row.insurance, 'missing'),
    license: text(row.license, 'not_required'), w9: text(row.w9, 'missing'), jobs: number(row.jobs),
    responseHours: number(row.responseHours), completionDays: number(row.completionDays),
    paidYtd: number(row.paidYtd), advanceOutstanding: number(row.advanceOutstanding),
  }
}

const normalizeAssignment = (value: unknown, index: number): VendorDemoAssignment => {
  const row = object(value)
  return { id: text(row.id, `demo-job-${index + 1}`), title: text(row.title, 'Demo assignment'), contractor: text(row.contractor, 'Demo contractor'), status: text(row.status, 'requested'), approved: number(row.approved), advance: number(row.advance), outstanding: number(row.outstanding) }
}

export function normalizeGuidedVendorDemo(value: unknown): { demo: GuidedVendorDemo | null; repaired: boolean } {
  const row = object(value)
  const id = text(row.id)
  if (!id) return { demo: null, repaired: true }
  const state = object(row.state)
  const progressSource = object(state.progress)
  const progress = Object.fromEntries(Object.entries(progressSource).filter(([,complete]) => complete === true)) as Partial<Record<GuidedVendorDemoAction, boolean>>
  const contractors = list(state.contractors).map(normalizeDemoContractor)
  const assignments = list(state.assignments).map(normalizeAssignment)
  const invitations = list(state.invitations).map((value, index) => { const item = object(value); return { id: text(item.id, `demo-invite-${index + 1}`), name: text(item.name, 'Demo contractor'), status: text(item.status, 'sent'), email: text(item.email, 'demo-provider@example.invalid') } })
  const requests = list(state.requests).map((value, index) => { const item = object(value); return { id: text(item.id, `demo-request-${index + 1}`), title: text(item.title, 'Demo service request'), property: text(item.property, 'Demo property'), status: text(item.status, 'requested'), contractor: text(item.contractor, 'Unassigned') } })
  const ledger = list(state.ledger).map((value, index) => { const item = object(value); return { id: text(item.id, `demo-ledger-${index + 1}`), contractor: text(item.contractor, 'Demo contractor'), kind: text(item.kind, 'payment'), amount: number(item.amount), date: text(item.date) } })
  const alerts = list(state.alerts).filter((item): item is string => typeof item === 'string')
  const demo: GuidedVendorDemo = {
    id, name: text(row.name, 'Guided Demo'), locale: row.locale === 'es' ? 'es' : 'en',
    scenarioVersion: text(row.scenarioVersion, GUIDED_DEMO_SCHEMA_VERSION), resetCount: number(row.resetCount),
    lastResetAt: text(row.lastResetAt), state: { organization: text(state.organization, 'PropertyReady Demo Property Management'), demo: true, paymentsSynthetic: true, progress, contractors, invitations, requests, assignments, ledger, alerts, tourStep: number(state.tourStep) },
  }
  const repaired = row.scenarioVersion !== GUIDED_DEMO_SCHEMA_VERSION || !state.progress || !Array.isArray(state.contractors) || !Array.isArray(state.requests) || !Array.isArray(state.assignments) || !Array.isArray(state.ledger) || !Array.isArray(state.alerts)
  return { demo, repaired }
}

const normalizePrivateVendor = (value: unknown, index: number): PrivateVendor => {
  const row = object(value)
  const compliance = object(row.compliance)
  return {
    userId: text(row.userId, `unavailable-vendor-${index + 1}`), businessName: text(row.businessName, 'Contractor'),
    status: ['invited','onboarding','pending_review','active','restricted','suspended','removed'].includes(text(row.status)) ? text(row.status) as PrivateVendor['status'] : 'pending_review',
    visibility: ['private_organization','selected_organizations','public_marketplace_eligible'].includes(text(row.visibility)) ? text(row.visibility) as PrivateVendor['visibility'] : 'private_organization',
    preferred: boolean(row.preferred), ranking: typeof row.ranking === 'number' ? row.ranking : null, doNotUse: boolean(row.doNotUse),
    categories: list(row.categories).filter((item): item is string => typeof item === 'string'),
    compliance: { insurance: compliance.insurance === 'valid' ? 'valid' : 'action_required', license: compliance.license === 'valid' ? 'valid' : 'action_required', payment: compliance.payment === 'enabled' ? 'enabled' : 'action_required' },
    lastJob: typeof row.lastJob === 'string' ? row.lastJob : null, openAssignments: number(row.openAssignments), paidYtdCents: number(row.paidYtdCents), alerts: number(row.alerts),
  }
}

export function normalizePrivateVendorWorkspace(value: unknown): PrivateVendorWorkspace {
  const row = object(value)
  const flags = object(row.flags)
  const invitations = list(row.invitations).map((value, index): VendorInvitation => {
    const item = object(value)
    const status = text(item.status)
    const visibility = text(item.visibility)
    return {
      id: text(item.id, `unavailable-invitation-${index + 1}`), email: typeof item.email === 'string' ? item.email : null, mobile: typeof item.mobile === 'string' ? item.mobile : null,
      status: ['pending','accepted','expired','revoked'].includes(status) ? status as VendorInvitation['status'] : 'pending',
      visibility: ['private_organization','selected_organizations','public_marketplace_eligible'].includes(visibility) ? visibility as VendorInvitation['visibility'] : 'private_organization',
      expiresAt: text(item.expiresAt), resendCount: number(item.resendCount),
    }
  })
  return {
    flags: Object.fromEntries(Object.entries(flags).map(([key,enabled]) => [key, enabled === true])),
    invitations, contractors: list(row.contractors).map(normalizePrivateVendor),
    expiringCount: number(row.expiringCount), restrictedCount: number(row.restrictedCount), paymentActionCount: number(row.paymentActionCount),
  }
}
